import { Outlet, Link, useLocation } from 'react-router-dom'
import { useMemo } from 'react'
import { useSessionContext } from '@/components/SessionProvider'
import { signOut, getUserRoles } from '@/lib/auth'
import { cn } from '@/lib/utils'
import {
  Home,
  FileText,
  Building2,
  Users,
  LogOut,
  HeartHandshake,
  User,
} from 'lucide-react'

type NavItem = {
  name: string
  href: string
  icon: typeof Home
  roles?: string[]
}

const allNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Reports', href: '/field-reports', icon: FileText },
  { name: 'Rooms', href: '/soft-rooms', icon: Building2, roles: ['admin', 'soft_room_responder', 'case_manager'] },
  { name: 'Sessions', href: '/sessions', icon: Users, roles: ['admin', 'soft_room_responder', 'case_manager'] },
  { name: 'Partners', href: '/partners', icon: HeartHandshake, roles: ['admin', 'soft_room_responder', 'case_manager'] },
]

function getPortalSubtitle(pathname: string): string {
  if (pathname === '/' || pathname === '/dashboard') return 'Operations Dashboard'
  if (pathname.startsWith('/field-reports')) return 'Field Report Portal'
  if (pathname.startsWith('/soft-rooms')) return 'Soft Room Portal'
  if (pathname.startsWith('/sessions')) return 'Soft Room Portal'
  if (pathname.startsWith('/partners')) return 'Referral Directory'
  if (pathname.startsWith('/profile')) return 'User Profile'
  return 'Sanctuary'
}

export default function Layout() {
  const location = useLocation()
  const { session } = useSessionContext()

  const navigation = useMemo(() => {
    const roles = getUserRoles(session)
    if (roles.length === 0) return allNavigation
    return allNavigation.filter(item => {
      if (!item.roles) return true
      return item.roles.some(r => roles.includes(r))
    })
  }, [session])

  const userName = session?.user?.name || session?.user?.email || 'User'
  const subtitle = getPortalSubtitle(location.pathname)

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-between items-center">
          {/* Left: Logo + Title */}
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/sanctuary-logo.png" alt="Sanctuary" className="h-10 w-auto" />
            <div className="hidden sm:block">
              <h1 className="font-bold tracking-tight text-sm text-foreground">Sanctuary</h1>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{subtitle}</p>
            </div>
          </Link>

          {/* Right: User Info + Logout */}
          <div className="flex items-center gap-2">
            {/* Desktop */}
            <div className="hidden sm:flex items-center gap-3">
              <Link to="/profile" className="text-right hover:opacity-80 transition-opacity">
                <p className="text-sm font-medium">{userName}</p>
                <p className="text-[10px] text-muted-foreground">{session?.user?.email}</p>
              </Link>
              <button
                onClick={() => signOut()}
                className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <LogOut size={14} />
                Logout
              </button>
            </div>

            {/* Mobile */}
            <Link
              to="/profile"
              className="sm:hidden flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary"
            >
              <User size={18} />
            </Link>
          </div>
        </div>

        {/* Desktop horizontal nav */}
        <div className="hidden md:block border-t border-border/50">
          <div className="max-w-4xl mx-auto px-4">
            <nav className="flex gap-1 -mb-px">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href ||
                  (item.href !== '/' && location.pathname.startsWith(item.href))
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors',
                      isActive
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    )}
                  >
                    <item.icon className="h-3.5 w-3.5" />
                    {item.name}
                  </Link>
                )
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content — centered like v0 */}
      <main className="flex-1 w-full max-w-4xl mx-auto px-4 py-6 pb-24 md:pb-6">
        <Outlet />
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-border md:hidden z-50 shadow-[0_-1px_3px_rgba(0,0,0,0.05)]">
        <div className="max-w-4xl mx-auto flex justify-around items-center py-2">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href ||
              (item.href !== '/' && location.pathname.startsWith(item.href))
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors min-w-[3.5rem]',
                  isActive ? 'text-primary' : 'text-muted-foreground'
                )}
              >
                <item.icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{item.name}</span>
              </Link>
            )
          })}
        </div>
      </nav>

      {/* Desktop Footer */}
      <footer className="hidden md:block text-center text-xs text-muted-foreground py-6 border-t border-border bg-white">
        <p className="uppercase tracking-widest">
          Confidential Data: Strictly for Authorized Law Enforcement and NGO Personnel
        </p>
      </footer>
    </div>
  )
}
